import JSZip from "jszip";

// IMDF 檔案類型定義
export interface IMDFFileTypes {
  building?: GeoJSON.FeatureCollection;
  level?: GeoJSON.FeatureCollection;
  unit?: GeoJSON.FeatureCollection;
  amenity?: GeoJSON.FeatureCollection;
  anchor?: GeoJSON.FeatureCollection;
  opening?: GeoJSON.FeatureCollection;
  fixture?: GeoJSON.FeatureCollection;
  kiosk?: GeoJSON.FeatureCollection;
  occupant?: GeoJSON.FeatureCollection;
  detail?: GeoJSON.FeatureCollection;
  relationship?: GeoJSON.FeatureCollection;
  venue?: GeoJSON.FeatureCollection;
  address?: GeoJSON.FeatureCollection;
  footprint?: GeoJSON.FeatureCollection;
  section?: GeoJSON.FeatureCollection;
  geofence?: GeoJSON.FeatureCollection;
  manifest?: any;
}

// IMDF 物件樣式配置
export const IMDF_STYLES = {
  building: {
    color: "#ff7800",
    weight: 2,
    fillOpacity: 0.3,
    fillColor: "#ff7800",
  },
  level: {
    color: "#0078ff",
    weight: 1,
    fillOpacity: 0.2,
    fillColor: "#0078ff",
  },
  unit: { color: "#002020", weight: 1, fillOpacity: 0.8, fillColor: "#006644" },
  amenity: {
    color: "#aa0078",
    weight: 1,
    fillOpacity: 0.6,
    fillColor: "#dd0056",
  },
  opening: {
    color: "#ffaa00",
    weight: 1,
    fillOpacity: 0.6,
    fillColor: "#ffaa00",
  },
  fixture: {
    color: "#aa00ff",
    weight: 1,
    fillOpacity: 0.4,
    fillColor: "#aa00ff",
  },
  anchor: {
    color: "#00aaff",
    weight: 3,
    fillOpacity: 0.8,
    fillColor: "#00aaff",
  },
  kiosk: {
    color: "#ff5500",
    weight: 2,
    fillOpacity: 0.6,
    fillColor: "#ff5500",
  },
  occupant: {
    color: "#44a0ef",
    weight: 2,
    fillOpacity: 0.6,
    fillColor: "#8044ee",
  },
  detail: {
    color: "#ffff00",
    weight: 1,
    fillOpacity: 0.2,
    fillColor: "#ffff00",
  },
  relationship: {
    color: "#ff00ff",
    weight: 1,
    fillOpacity: 0.1,
    fillColor: "#ff00ff",
  },
  venue: {
    color: "#00ffff",
    weight: 3,
    fillOpacity: 0.2,
    fillColor: "#00ffff",
  },
  address: {
    color: "#808080",
    weight: 1,
    fillOpacity: 0.3,
    fillColor: "#808080",
  },
  footprint: {
    color: "#964B00",
    weight: 2,
    fillOpacity: 0.3,
    fillColor: "#964B00",
  },
  section: {
    color: "#FFC0CB",
    weight: 1,
    fillOpacity: 0.3,
    fillColor: "#FFC0CB",
  },
  geofence: {
    color: "#800080",
    weight: 2,
    fillOpacity: 0.1,
    fillColor: "#800080",
  },
  default: {
    color: "#3388ff",
    weight: 2,
    fillOpacity: 0.3,
    fillColor: "#3388ff",
  },
};

// IMDF 物件優先級 (用於圖層顯示順序)
export const IMDF_LAYER_ORDER = [
  "venue",
  "building",
  "footprint",
  "level",
  "section",
  "unit",
  "opening",
  "fixture",
  "amenity",
  "kiosk",
  "anchor",
  "detail",
  "occupant",
  "address",
  "geofence",
  "relationship",
];

/**
 * 解析 IMDF ZIP 檔案
 */
export class IMDFParser {
  private zipData: JSZip | null = null;
  private parsedData: IMDFFileTypes = {};

  /**
   * 載入 ZIP 檔案
   */
  async loadZipFile(file: File | Blob): Promise<void> {
    try {
      this.zipData = await JSZip.loadAsync(file);
      this.parsedData = {};
    } catch (error) {
      throw new Error(`Failed to load ZIP file: ${error}`);
    }
  }

  /**
   * 解析所有 IMDF 檔案
   */
  async parseAllFiles(): Promise<IMDFFileTypes> {
    if (!this.zipData) {
      throw new Error("No ZIP file loaded");
    }

    const results: IMDFFileTypes = {};

    // 解析 manifest.json
    const manifestFile = this.zipData.file("manifest.json");
    if (manifestFile) {
      try {
        const manifestContent = await manifestFile.async("text");
        results.manifest = JSON.parse(manifestContent);
      } catch (error) {
        console.warn("Failed to parse manifest.json:", error);
      }
    }

    // 解析各種 IMDF GeoJSON 檔案
    const imdfFileTypes = [
      "building",
      "level",
      "unit",
      "amenity",
      "anchor",
      "opening",
      "fixture",
      "kiosk",
      "occupant",
      "detail",
      "relationship",
      "venue",
      "address",
      "footprint",
      "section",
      "geofence",
    ];

    for (const fileType of imdfFileTypes) {
      const fileName = `${fileType}.geojson`;
      const file = this.zipData.file(fileName);

      if (file) {
        try {
          const content = await file.async("text");
          const geoJson = JSON.parse(content) as GeoJSON.FeatureCollection;

          // 為每個特徵添加類型資訊
          geoJson.features.forEach((feature) => {
            if (feature.properties) {
              feature.properties._file_type = fileType;
              feature.properties._source_file = fileName;
            }
          });

          results[fileType as keyof IMDFFileTypes] = geoJson;
        } catch (error) {
          console.warn(`Failed to parse ${fileName}:`, error);
        }
      }
    }

    this.parsedData = results;
    return results;
  }

  /**
   * 獲取解析後的資料
   */
  getParsedData(): IMDFFileTypes {
    return this.parsedData;
  }

  /**
   * 獲取合併的 GeoJSON 資料
   */
  getCombinedGeoJSON(): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];

    // 按照優先級順序添加特徵
    for (const layerType of IMDF_LAYER_ORDER) {
      const layerData = this.parsedData[
        layerType as keyof IMDFFileTypes
      ] as GeoJSON.FeatureCollection;
      if (layerData?.features) {
        features.push(...layerData.features);
      }
    }

    return {
      type: "FeatureCollection",
      features,
    };
  }

  /**
   * 獲取指定類型的 GeoJSON 資料
   */
  getLayerData(layerType: string): GeoJSON.FeatureCollection | null {
    return (
      (this.parsedData[
        layerType as keyof IMDFFileTypes
      ] as GeoJSON.FeatureCollection) || null
    );
  }

  /**
   * 獲取可用的圖層列表
   */
  getAvailableLayers(): string[] {
    return Object.keys(this.parsedData).filter((key) => key !== "manifest");
  }

  /**
   * 獲取樓層資訊
   */
  getFloorLevels(): Array<{ ordinal: number; name: string; id: string }> {
    const levelData = this.parsedData.level;
    if (!levelData?.features) return [];

    return levelData.features
      .map((feature) => ({
        ordinal: feature.properties?.ordinal || 0,
        name:
          this.extractDisplayName(feature.properties?.name) ||
          `Level ${feature.properties?.ordinal || 0}`,
        id: feature.properties?.id || (feature.id as string) || "",
      }))
      .sort((a, b) => b.ordinal - a.ordinal); // 從高樓層到低樓層排序
  }

  /**
   * 提取顯示名稱，處理多語言格式
   */
  private extractDisplayName(name: any): string {
    if (typeof name === "string") {
      return name;
    }
    if (typeof name === "object" && name !== null) {
      // 嘗試不同語言，優先中文，然後英文，最後取第一個可用的值
      return (
        name.zh ||
        name["zh-TW"] ||
        name["zh-CN"] ||
        name.en ||
        name["en-US"] ||
        name["en-GB"] ||
        Object.values(name)[0] ||
        ""
      );
    }
    return "";
  }

  /**
   * 根據樓層過濾特徵
   */
  getFeaturesByLevel(levelId: string): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];

    // 收集與指定樓層相關的所有特徵
    for (const layerType of IMDF_LAYER_ORDER) {
      const layerData = this.parsedData[
        layerType as keyof IMDFFileTypes
      ] as GeoJSON.FeatureCollection;
      if (layerData?.features) {
        const layerFeatures = layerData.features.filter((feature) => {
          // 檢查特徵是否屬於指定樓層
          return (
            feature.properties?.level_id === levelId ||
            feature.properties?.id === levelId ||
            (layerType === "level" && feature.properties?.id === levelId)
          );
        });
        features.push(...layerFeatures);
      }
    }

    return {
      type: "FeatureCollection",
      features,
    };
  }

  /**
   * 搜尋特徵
   */
  searchFeatures(query: string): GeoJSON.Feature[] {
    const features: GeoJSON.Feature[] = [];
    const lowerQuery = query.toLowerCase();

    for (const layerType of IMDF_LAYER_ORDER) {
      const layerData = this.parsedData[
        layerType as keyof IMDFFileTypes
      ] as GeoJSON.FeatureCollection;
      if (layerData?.features) {
        const matchingFeatures = layerData.features.filter((feature) => {
          if (!feature.properties) return false;

          return Object.values(feature.properties).some((value) =>
            String(value).toLowerCase().includes(lowerQuery)
          );
        });
        features.push(...matchingFeatures);
      }
    }

    return features;
  }

  /**
   * 驗證 IMDF 檔案結構
   */
  validateIMDFStructure(): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 檢查是否有必要的檔案
    if (!this.parsedData.building) {
      errors.push("Missing required building.geojson file");
    }

    if (!this.parsedData.level) {
      warnings.push("Missing level.geojson file");
    }

    if (!this.parsedData.manifest) {
      warnings.push("Missing manifest.json file");
    }

    // 檢查特徵的必要屬性
    Object.entries(this.parsedData).forEach(([layerType, layerData]) => {
      if (layerType === "manifest") return;

      const geoJsonData = layerData as GeoJSON.FeatureCollection;
      if (geoJsonData?.features) {
        geoJsonData.features.forEach((feature, index) => {
          if (!feature.properties?.id) {
            errors.push(
              `${layerType} feature ${index} missing required 'id' property`
            );
          }
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 匯出修改後的 IMDF 檔案
   */
  async exportToZip(): Promise<Blob> {
    const zip = new JSZip();

    // 添加 manifest.json
    if (this.parsedData.manifest) {
      zip.file(
        "manifest.json",
        JSON.stringify(this.parsedData.manifest, null, 2)
      );
    }

    // 添加各種 GeoJSON 檔案
    Object.entries(this.parsedData).forEach(([layerType, layerData]) => {
      if (layerType === "manifest") return;

      const geoJsonData = layerData as GeoJSON.FeatureCollection;
      if (geoJsonData) {
        // 清理特徵屬性（移除內部屬性）
        const cleanedData = {
          ...geoJsonData,
          features: geoJsonData.features.map((feature) => ({
            ...feature,
            properties: Object.fromEntries(
              Object.entries(feature.properties || {}).filter(
                ([key]) => !key.startsWith("_")
              )
            ),
          })),
        };

        zip.file(`${layerType}.geojson`, JSON.stringify(cleanedData, null, 2));
      }
    });

    return await zip.generateAsync({ type: "blob" });
  }
}

/**
 * 獲取 IMDF 物件的樣式
 */
export function getIMDFStyle(feature: GeoJSON.Feature): any {
  const featureType =
    feature.properties?._file_type || feature.properties?.category || "default";
  const baseStyle =
    IMDF_STYLES[featureType as keyof typeof IMDF_STYLES] || IMDF_STYLES.default;

  const featureId = feature.properties?.id || feature.id;
  const isSelected = feature.properties?._selected;
  const isHighlighted = feature.properties?._highlighted_as_related;
  const isContext = feature.properties?._is_context; // level/unit 背景圖層
  const isDimmed = feature.properties?._is_dimmed; // 非相關的 unit 要變淡

  // 如果 feature 被選中，增強顯示
  if (isSelected) {
    return {
      ...baseStyle,
      weight: baseStyle.weight + 3,
      color: "#000000", // 被選中的顏色
      fillOpacity: Math.min(baseStyle.fillOpacity + 0.3, 0.8),
    };
  }

  // 如果 feature 是作為關聯項被高亮顯示（例如 amenity 的相關 unit）
  if (isHighlighted) {
    return {
      ...baseStyle,
      weight: baseStyle.weight + 2,
      color: "#ffaa00", // 橙色邊框表示關聯
      fillOpacity: Math.min(baseStyle.fillOpacity + 0.2, 0.7),
      dashArray: "5, 10", // 虛線邊框
    };
  }

  // 如果是背景圖層（level/unit）
  if (isContext) {
    if (isDimmed) {
      // 非相關的 unit：很淡的顯示
      return {
        ...baseStyle,
        weight: 1,
        color: "#cccccc",
        fillOpacity: 0.1,
        opacity: 0.3,
      };
    } else {
      // 相關的 unit 或 level：較淡但可見
      return {
        ...baseStyle,
        weight: featureType === "level" ? 2 : 1,
        fillOpacity: featureType === "level" ? 0.15 : 0.25,
        opacity: 0.6,
      };
    }
  }

  return baseStyle;
}

/**
 * 獲取 IMDF 物件的顯示名稱
 */
export function getIMDFDisplayName(feature: GeoJSON.Feature): string {
  const props = feature.properties;
  if (!props) return "Unknown Feature";

  // 使用輔助函數處理可能的多語言名稱
  const name = extractDisplayNameFromProperty(props.name);
  const displayName = extractDisplayNameFromProperty(props.display_name);

  return (
    name ||
    displayName ||
    props.id ||
    `${props._file_type || "Feature"} ${props.id || ""}`
  );
}

/**
 * 從屬性中提取顯示名稱，處理多語言格式
 */
function extractDisplayNameFromProperty(name: any): string {
  if (typeof name === "string") {
    return name;
  }
  if (typeof name === "object" && name !== null) {
    // 嘗試不同語言，優先中文，然後英文，最後取第一個可用的值
    return (
      name.zh ||
      name["zh-TW"] ||
      name["zh-CN"] ||
      name.en ||
      name["en-US"] ||
      name["en-GB"] ||
      Object.values(name)[0] ||
      ""
    );
  }
  return "";
}

/**
 * 檢查兩個特徵是否有關聯
 */
export function getFeaturesRelationship(
  feature1: GeoJSON.Feature,
  feature2: GeoJSON.Feature
): string | null {
  const props1 = feature1.properties;
  const props2 = feature2.properties;

  if (!props1 || !props2) return null;

  // 檢查父子關係
  if (
    props1.building_id === props2.id ||
    props1.level_id === props2.id ||
    props1.unit_id === props2.id
  ) {
    return "child";
  }

  if (
    props2.building_id === props1.id ||
    props2.level_id === props1.id ||
    props2.unit_id === props1.id
  ) {
    return "parent";
  }

  // 檢查同級關係
  if (
    props1.building_id === props2.building_id &&
    props1.level_id === props2.level_id
  ) {
    return "sibling";
  }

  return null;
}
